import { useMemo, useState } from "react";
import { Switch } from "@design-system/Switch/Switch";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import {
    componentAnatomySpecs,
    type AnatomyPartId,
    type AnatomyStateId,
    type ComponentAnatomyId,
} from "./componentAnatomy";
import {
    AnatomyCombinationGap,
    ComponentAnatomyView,
} from "./ComponentAnatomyView";
import type { AvatarSchematicContent } from "./ComponentAnatomySchematicAvatar";
import type { FieldSchematicContent } from "./ComponentAnatomySchematicField";
import type { InputSchematicContent } from "./ComponentAnatomySchematicInput";
import type { KickLogoSchematicContent } from "./ComponentAnatomySchematicKickLogo";
import type { PickerSchematicContent } from "./ComponentAnatomySchematicPicker";
import type { SwitchSchematicContent } from "./ComponentAnatomySchematicSwitch";
import type { TagSchematicContent } from "./ComponentAnatomySchematicTag";
import type { TagsInputSchematicContent } from "./ComponentAnatomySchematicTagsInput";
import type { TextAreaSchematicContent } from "./ComponentAnatomySchematicTextArea";
import type { StageWidth } from "./ShowcasePreview";
import styles from "./ComponentAnatomyView.module.scss";

/**
 * @purpose Turns a component playground into an x-ray of itself without moving
 * anything: the aside is the same in both modes, the stage keeps its geometry,
 * and only what stands on the stage swaps.
 *
 * One set of knobs drives both pictures. Variant, checked value and the disabled
 * prop come from the playground's own controls; the state section this hook adds
 * feeds the diagram and, through the stylesheets' `data-forced-state` hook, the
 * live component too — so it earns its place in the aside in either mode.
 */

/** The playground-only contract every one of the three stylesheets mirrors. */
export type AnatomyForcedState = "hover" | "pressed" | "focus";

/**
 * The schematic and its legend need the stage's whole width to sit side by side.
 * Only the box inside the stage widens, and only while the x-ray is on, so the
 * stage itself and everything around it stay put.
 */
const xrayStageWidth: StageWidth = "full";
const liveStageWidth: StageWidth = "default";

const toForcedState = (
    stateId: AnatomyStateId,
): AnatomyForcedState | undefined => {
    switch (stateId) {
        case "hover":
        case "pressed":
        case "focus":
            return stateId;
        default:
            return undefined;
    }
};

export const useAnatomyMode = ({
    component,
    variantId,
    sizeId,
    isDisabled,
    isReadOnly = false,
    isInvalid = false,
    tagContent,
    fieldContent,
    inputContent,
    textAreaContent,
    pickerContent,
    tagsInputContent,
    avatarContent,
    kickLogoContent,
    switchContent,
}: {
    component: ComponentAnatomyId;
    /** Spec variant the playground's own controls select. */
    variantId: string;
    /** Spec size the playground's own size control selects, where it has one. */
    sizeId?: string;
    /** The playground's disabled prop, which owns the disabled state here. */
    isDisabled: boolean;
    isReadOnly?: boolean;
    isInvalid?: boolean;
    /** Tag playground feature state, so its schematic mirrors the live tag. */
    tagContent?: TagSchematicContent;
    /** Field playground content and feature state mirrored by its schematic. */
    fieldContent?: FieldSchematicContent;
    /** Input playground feature state, so its schematic mirrors the live input. */
    inputContent?: InputSchematicContent;
    /** Text area playground feature state, so its schematic mirrors the live field. */
    textAreaContent?: TextAreaSchematicContent;
    /** Picker playground feature state, so its schematic mirrors the live picker. */
    pickerContent?: PickerSchematicContent;
    /** Tags input playground values, so its schematic draws the same chips. */
    tagsInputContent?: TagsInputSchematicContent;
    /** Avatar playground size, so its schematic mirrors the live avatar. */
    avatarContent?: AvatarSchematicContent;
    /** KickLogo playground size, so its schematic mirrors the live logo. */
    kickLogoContent?: KickLogoSchematicContent;
    /** Switch playground label state, so its schematic draws the same row. */
    switchContent?: SwitchSchematicContent;
}) => {
    const spec = componentAnatomySpecs[component];
    const [isActive, setIsActive] = useState(false);
    const [interactionStateId, setInteractionStateId] =
        useState<AnatomyStateId>("resting");
    const [activePartId, setActivePartId] = useState<AnatomyPartId | null>(
        null,
    );

    const highlight = useMemo(
        () => ({ activePartId, onPartActivate: setActivePartId }),
        [activePartId],
    );

    const variant = spec.variants.find((entry) => entry.id === variantId);
    const size = spec.sizes?.find((entry) => entry.id === sizeId);
    const isSizeMissing = spec.sizes !== undefined && size === undefined;

    // Every interactive rule in these stylesheets is guarded against the
    // disabled state, so a disabled component can never be repainted by hover,
    // press or focus. The prop wins in both pictures and the toggle goes inert.
    let stateId: AnatomyStateId = interactionStateId;
    if (isInvalid) {
        stateId = "invalid";
    }
    if (isReadOnly) {
        stateId = "readOnly";
    }
    if (isDisabled) {
        stateId = "disabled";
    }
    const isInteractionLocked = isDisabled || isReadOnly || isInvalid;
    const interactionStates = spec.states.filter(
        (entry) =>
            entry.id !== "disabled" &&
            entry.id !== "readOnly" &&
            entry.id !== "invalid",
    );

    const control = (
        <>
            <section>
                <h2>X-ray</h2>
                <Switch
                    checked={isActive}
                    label="Show token anatomy"
                    onCheckedChange={(nextIsActive) => {
                        setIsActive(nextIsActive);
                        setActivePartId(null);
                    }}
                />
            </section>
            <section>
                <h2>State</h2>
                <ToggleGroup.Root<AnatomyStateId>
                    className={styles.switcher}
                    value={isInteractionLocked ? null : interactionStateId}
                    disabled={isInteractionLocked}
                    onValueChange={(nextStateId) => {
                        if (nextStateId) {
                            setInteractionStateId(nextStateId);
                        }
                    }}
                >
                    {interactionStates.map((state) => (
                        <ToggleGroup.Item key={state.id} value={state.id}>
                            {state.label}
                        </ToggleGroup.Item>
                    ))}
                </ToggleGroup.Root>
            </section>
        </>
    );

    const view =
        variant === undefined || isSizeMissing ? (
            <AnatomyCombinationGap
                spec={spec}
                missing={
                    variant === undefined
                        ? variantId
                        : `${variantId} at ${sizeId}`
                }
            />
        ) : (
            <ComponentAnatomyView
                spec={spec}
                variant={variant}
                size={size}
                stateId={stateId}
                highlight={highlight}
                tagContent={tagContent}
                fieldContent={fieldContent}
                inputContent={inputContent}
                textAreaContent={textAreaContent}
                pickerContent={pickerContent}
                tagsInputContent={tagsInputContent}
                avatarContent={avatarContent}
                kickLogoContent={kickLogoContent}
                switchContent={switchContent}
            />
        );

    return {
        isActive,
        control,
        stageWidth: isActive ? xrayStageWidth : liveStageWidth,
        /** Feed to the live component so the state section drives it too. */
        forcedState: isInteractionLocked
            ? undefined
            : toForcedState(interactionStateId),
        view: isActive ? view : null,
    };
};
